/**
 * Client-side trigger watcher — port of legacy
 * `storyscale-widgets/src/components/targeting/index.vue`.
 *
 * Takes the trigger list returned by /v1/discover and installs the
 * appropriate listener per type. First trigger to fire wins — once any
 * trigger fires, all remaining listeners are torn down. Cleanup is
 * idempotent so the caller can also tear down on page unload.
 *
 * Trigger types (parity with legacy):
 *   - page_load    : synchronous — fires on the next tick
 *   - utm          : synchronous — checks window.location.search
 *   - time_on_page : setTimeout(meta.duration * 1000)
 *   - scroll_depth : window scroll listener; fires when
 *                    (scrollY + innerHeight) / scrollHeight * 100 >= meta.percentage
 *   - exit_intent  : document mousemove listener; fires when e.clientY <= 20
 *   - element      : per-selector click listener (legacy only ever shipped click;
 *                    `exists`/`visible` were schema-only and not implemented)
 */

export type WatcherTrigger = {
  type: 'page_load' | 'time_on_page' | 'scroll_depth' | 'exit_intent' | 'utm' | 'element';
  meta: {
    duration?: number;
    percentage?: number;
    property?: string;
    value?: string;
    elementSelector?: string;
    elementTriggerType?: 'exists' | 'visible' | 'click';
    elementWidgetType?: 'launcher' | 'embed';
    /** Presence anchor — forwarded to autoMount on fire. */
    position?: 'right' | 'center';
  };
  agentPublicId: string;
  view: string;
  /** The deployment (placement) this trigger belongs to — echoed on
   * presence events so the funnel attributes per placement. Optional:
   * older chat-api builds don't emit it. */
  deploymentId?: string;
};

export type TriggerWatcherHandle = {
  /** Tear down all listeners + timers. Idempotent. */
  cleanup: () => void;
  /**
   * For tests: synchronously dispatch any triggers that fire on boot
   * (page_load, utm). Real callers don't need to invoke — `install` does
   * it via microtask so the caller can attach `cleanup` first.
   */
  flushSync: () => void;
};

/**
 * Install watchers for the supplied triggers. Returns a handle whose
 * `cleanup()` removes every listener + clears every timer the watcher
 * created. `onFire` is invoked at most ONCE (first-fired-wins, matching
 * legacy `isExperienceActive` semantics).
 */
export function installTriggerWatchers(
  triggers: WatcherTrigger[],
  onFire: (t: WatcherTrigger) => void,
  options?: { now?: () => number; win?: Window; doc?: Document },
): TriggerWatcherHandle {
  const win = options?.win ?? (typeof window !== 'undefined' ? window : undefined);
  const doc = options?.doc ?? (typeof document !== 'undefined' ? document : undefined);

  let active = false;
  const cleanups: Array<() => void> = [];
  const fired = new Set<string>();

  function fire(t: WatcherTrigger): void {
    if (active) return;
    const key = `${t.type}:${t.agentPublicId}`;
    if (fired.has(key)) return;
    fired.add(key);
    active = true;
    cleanup();
    try {
      onFire(t);
    } catch (err) {
      console.warn('[showpilot] trigger watcher onFire threw', err);
    }
  }

  function cleanup(): void {
    while (cleanups.length > 0) {
      const fn = cleanups.shift();
      try {
        fn?.();
      } catch {
        /* swallow — cleanups must never throw */
      }
    }
  }

  // Synchronous-pass triggers (utm, page_load). UTM checks the URL
  // first — legacy honored UTM ahead of page_load to allow campaign
  // overrides on top of a default page_load agent.
  function flushSync(): void {
    if (active) return;
    if (win) {
      const params = new URLSearchParams(win.location.search);
      for (const t of triggers) {
        if (t.type !== 'utm') continue;
        const prop = t.meta.property;
        const want = t.meta.value;
        if (!prop || !want) continue;
        if (params.get(prop) === want) {
          fire(t);
          return;
        }
      }
    }
    for (const t of triggers) {
      if (active) return;
      if (t.type === 'page_load') {
        fire(t);
        return;
      }
    }
  }

  // Async-pass: install one watcher per non-synchronous trigger.
  for (const t of triggers) {
    switch (t.type) {
      case 'time_on_page': {
        const ms = Math.max(0, (t.meta.duration ?? 0) * 1000);
        const timer = (win ?? globalThis).setTimeout(() => fire(t), ms) as number;
        cleanups.push(() => (win ?? globalThis).clearTimeout(timer));
        break;
      }
      case 'scroll_depth': {
        if (!win || !doc) break;
        const target = Math.max(5, Math.min(100, t.meta.percentage ?? 50));
        const onScroll = (): void => {
          const top = win.scrollY ?? win.pageYOffset ?? 0;
          const view = win.innerHeight ?? 0;
          const total = doc.documentElement.scrollHeight ?? 1;
          const pct = ((top + view) / total) * 100;
          if (pct >= target) fire(t);
        };
        win.addEventListener('scroll', onScroll, { passive: true });
        cleanups.push(() => win.removeEventListener('scroll', onScroll));
        break;
      }
      case 'exit_intent': {
        if (!doc) break;
        const onMove = (e: MouseEvent): void => {
          if (e.clientY <= 20) fire(t);
        };
        doc.addEventListener('mousemove', onMove);
        cleanups.push(() => doc.removeEventListener('mousemove', onMove));
        break;
      }
      case 'element': {
        if (!doc) break;
        const sel = t.meta.elementSelector;
        if (!sel) break;
        // Legacy only implemented 'click'. We mirror that until v3 needs
        // exists/visible (would use IntersectionObserver / MutationObserver).
        const kind = t.meta.elementTriggerType ?? 'click';
        if (kind !== 'click') break;
        let nodes: Element[];
        try {
          nodes = Array.from(doc.querySelectorAll(sel));
        } catch (err) {
          console.warn(`[showpilot] invalid selector ${sel}`, err);
          break;
        }
        for (const node of nodes) {
          const onClick = (e: Event): void => {
            e.preventDefault();
            fire(t);
          };
          node.addEventListener('click', onClick);
          cleanups.push(() => node.removeEventListener('click', onClick));
        }
        break;
      }
      // page_load / utm handled synchronously in flushSync.
      default:
        break;
    }
  }

  // Defer the synchronous pass so the caller can attach cleanup before
  // onFire runs (avoids dropping listeners if onFire navigates away).
  // Falls back to synchronous when no microtask host is available (test
  // harnesses can call flushSync() explicitly).
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(flushSync);
  } else {
    flushSync();
  }

  return { cleanup, flushSync };
}
